/**
 * Snapshot-based round state reducer.
 *
 * Single source of truth: every incoming snapshot (API, realtime)
 * is applied through this function. No merge — rawRound is replaced entirely.
 *
 * Version guard: if next.version <= prev.lastAcceptedVersion, snapshot is
 * ignored. This covers: bootstrap race (API vs Centrifugo), reconnect,
 * out-of-order delivery.
 *
 * Round number guard: if next.roundNum < prev.rawRound.roundNum, snapshot is
 * ignored (stale current-round / WS delivery behind the latest round).
 *
 * Derived state (viewState) is recalculated from the new snapshot.
 * Optimistic pending is reconciled against the accepted snapshot only.
 */

import {
  centidegreesToDegrees,
  getActiveBets,
  getEffectiveTargetCoordinate,
  getEffectiveTiming,
  normalizeUnixTimestampToMs,
  TRICK_TYPE_FREEZE_WHEEL,
} from "@/shared/api/types/pvpRoulette"
import {
  findSoloPartyForUser,
  isTeamRoundParty,
} from "@/shared/lib/pvpWheelUserIdentity"
import {
  EASING_KEY,
  SOLO_CURRENT_USER_COLOR,
  TEAM_1_COLOR,
} from "@/shared/constants/pvpRoulette"
import { getPvpRouletteRuntime } from "@/shared/lib/pvp-roulette-runtime/runtime"
import {
  applyTricksPhaseSectorColors,
  shouldApplyMutedWheelSectorColors,
  userParticipatesInWheelRound,
} from "@/shared/lib/pvpWheelTricksColors"
import { roundDiff, type DomainDiffEvent } from "./roundDiff"
import { deriveViewState } from "./deriveViewState"
import type {
  RoomSlice,
  OptimisticPendingBet,
  ReduceContext,
  OptimisticTrickPending,
} from "./types"
import type { PixiCommand, WheelParty } from "./commands"
import type {
  RoundSnapshot,
  RoundParty,
  TrickType,
} from "@/shared/api/types/pvpRoulette"

export interface ReduceResult {
  slice: RoomSlice
  commands: PixiCommand[]
}

/**
 * После HTTP оптимистика ждём строку в `next.tricks`; пока её нет, но уже есть чужой трик,
 * не сбрасываем `optimisticTrickPending` — иначе выкидывается Pixi-команда и ломается согласование с WS.
 */
export function reconcileOptimisticTrickPending(
  prev: OptimisticTrickPending | null,
  next: RoundSnapshot,
  nowMs: number
): OptimisticTrickPending | null {
  if (!prev) return null
  if (next.roundId !== prev.roundId) return null
  if (
    next.tricks.some(
      (t) => t.userId === prev.userId && t.type === prev.trickType
    )
  )
    return null
  if (next.tricks.some((t) => t.userId === prev.userId)) return null
  if (next.status !== "TRICKS" && next.status !== "SPINNING") return null
  if (
    nowMs - prev.submittedAt >
    getPvpRouletteRuntime().OPTIMISTIC_TRICK_TTL_MS
  )
    return null
  return prev
}

/** Только для `deriveViewState`: временно добавить ожидающий трик, пока сервер не эхнул. */
export function mergeSnapshotWithOptimisticTrickForView(
  next: RoundSnapshot,
  pending: OptimisticTrickPending | null
): RoundSnapshot {
  if (!pending || next.roundId !== pending.roundId) return next
  if (next.tricks.some((t) => t.userId === pending.userId)) return next
  return {
    ...next,
    tricks: [
      ...next.tricks,
      { userId: pending.userId, type: pending.trickType },
    ],
  }
}

/**
 * Optimistic trick Pixi commands use ids `optimistic:<ts>:<TrickType>`.
 * Drop when the server snapshot no longer carries that trick **and** we are not holding
 * `optimisticTrickPending` for the same round (interleaved WS: opponent trick first).
 */
export function filterStaleOptimisticTrickCommands(
  queue: PixiCommand[],
  next: RoundSnapshot,
  userId: string | undefined,
  pending: OptimisticTrickPending | null
): PixiCommand[] {
  if (queue.length === 0) return queue
  return queue.filter((c) => {
    if (!c.id.startsWith("optimistic:")) return true
    if (!userId) return false
    const parts = c.id.split(":")
    if (parts.length < 3 || parts[0] !== "optimistic") return true
    const trickType = parts.slice(2).join(":") as TrickType
    if (next.tricks.some((t) => t.userId === userId && t.type === trickType))
      return true
    if (
      pending &&
      pending.userId === userId &&
      pending.trickType === trickType &&
      pending.roundId === next.roundId &&
      (next.status === "TRICKS" || next.status === "SPINNING")
    ) {
      return true
    }
    return false
  })
}

/** End of TRICKS for wheel scheduling — infer when API omits tricks_ends_at. */
export function resolveTricksEndsAtMs(snap: RoundSnapshot): number | undefined {
  const t = getEffectiveTiming(snap)
  if (t.tricksEndsAt != null && Number.isFinite(t.tricksEndsAt)) {
    return normalizeUnixTimestampToMs(t.tricksEndsAt)
  }
  const sec = snap.tricksDurationSeconds
  if (sec != null && sec > 0) {
    if (t.tricksStartedAt != null && Number.isFinite(t.tricksStartedAt)) {
      return normalizeUnixTimestampToMs(t.tricksStartedAt) + sec * 1000
    }
    if (t.spinningEndsAt != null && Number.isFinite(t.spinningEndsAt)) {
      return normalizeUnixTimestampToMs(t.spinningEndsAt) + sec * 1000
    }
  }
  return undefined
}

/**
 * Reduce: apply next snapshot to previous slice.
 * Returns new slice and commands to emit.
 * Pure function — no module-level side effects.
 */
export function reduceRoomState(
  prev: RoomSlice,
  next: RoundSnapshot,
  ctx: ReduceContext
): ReduceResult {
  // Always accept first snapshot (bootstrap).
  const isFirstSnapshot = prev.rawRound == null
  // Drop only same-round stream regressions (same roundId, lower number). Different roundId + lower number
  // is valid (e.g. dev fixture replaying captured round 10 after round 11 without resetRoom).
  if (
    !isFirstSnapshot &&
    next.roundId === prev.rawRound.roundId &&
    next.roundNum < prev.rawRound.roundNum
  ) {
    return { slice: prev, commands: [] }
  }
  // New round ID starts an independent version timeline (can reset to 0/1).
  const isNewRound =
    prev.rawRound != null && prev.rawRound.roundId !== next.roundId
  if (
    !isFirstSnapshot &&
    !isNewRound &&
    next.version <= prev.lastAcceptedVersion
  ) {
    return { slice: prev, commands: [] }
  }

  const prevForReduce: RoomSlice = isNewRound
    ? {
        rawRound: null,
        viewState: null,
        commandsQueue: [],
        optimisticPending: null,
        optimisticTrickPending: null,
        lastAcceptedVersion: 0,
        activityFeed: [],
        clockOffsetMs: prev.clockOffsetMs,
      }
    : prev

  const diff = roundDiff(prevForReduce.rawRound, next)
  const rawRound = next
  const optimisticTrickPending = reconcileOptimisticTrickPending(
    prevForReduce.optimisticTrickPending ?? null,
    next,
    ctx.now
  )
  const snapshotForView = mergeSnapshotWithOptimisticTrickForView(
    next,
    optimisticTrickPending
  )
  const rctx = mergeReduceContext(prevForReduce, ctx)
  const viewState = deriveViewState(snapshotForView, {
    now: rctx.now,
    userId: rctx.userId,
    clockOffsetMs: rctx.clockOffsetMs,
  })
  const commands = generateCommands(diff, prevForReduce, next, rctx)
  const optimisticPending = reconcilePending(
    prevForReduce.optimisticPending,
    prevForReduce.rawRound,
    next,
    ctx.now,
    ctx.userId
  )
  const lastAcceptedVersion = next.version

  const cleanedQueue = filterStaleOptimisticTrickCommands(
    prevForReduce.commandsQueue,
    next,
    ctx.userId,
    optimisticTrickPending
  )

  const slice: RoomSlice = {
    rawRound,
    viewState,
    commandsQueue: [...cleanedQueue, ...commands],
    optimisticPending,
    optimisticTrickPending,
    lastAcceptedVersion,
    activityFeed: prevForReduce.activityFeed ?? [],
    clockOffsetMs: rctx.clockOffsetMs,
  }

  return { slice, commands }
}

/**
 * Deterministic command ID: version:type:index within a single reduce call.
 * Same inputs always produce the same IDs — reducer stays pure.
 */
function makeCommandId(
  roundVersion: number,
  type: string,
  idx: number
): string {
  return `${roundVersion}:${type}:${idx}`
}

/**
 * Длительность FinalizeToTarget в SLOWING — по серверным дедлайнам.
 * `nowMs` — aligned wall: `ctx.now + effective clockOffset` (тот же, что в `mergeReduceContext`).
 * Визуальная остановка: slowing_ends_at − slowing_offset_ms; остаток = visualStopMs − nowMs.
 * При остаток ≤ 0 duration не передаём — движок подставит краткий плейсхолдер до recalibrate.
 */
export function computeSlowingFinalizeDurationMs(
  nowMs: number,
  snapshot: RoundSnapshot
): number | undefined {
  const t = getEffectiveTiming(snapshot)
  const slowingEndsAt = t.slowingEndsAt
  if (slowingEndsAt == null || !Number.isFinite(slowingEndsAt)) return undefined

  const offsetMs = snapshot.slowingOffsetMs ?? 0
  // slowingEndsAt is now Unix ms — no * 1000 needed.
  const visualStopMs = slowingEndsAt - offsetMs
  const remainingMs = visualStopMs - nowMs
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return undefined
  return remainingMs
}

function getAlignedNowMs(nowMs: number, clockOffsetMs?: number): number {
  if (clockOffsetMs == null || !Number.isFinite(clockOffsetMs)) return nowMs
  return nowMs + clockOffsetMs
}

/** Merges incoming offset with slice; single place for `alignedNowMs` used by commands + viewState. */
function mergeReduceContext(
  prev: RoomSlice,
  ctx: ReduceContext
): ReduceContext {
  const effectiveClockOffsetMs =
    ctx.clockOffsetMs != null && Number.isFinite(ctx.clockOffsetMs)
      ? ctx.clockOffsetMs
      : prev.clockOffsetMs
  return {
    ...ctx,
    clockOffsetMs: effectiveClockOffsetMs,
    alignedNowMs: getAlignedNowMs(ctx.now, effectiveClockOffsetMs),
  }
}

/**
 * Map API snapshot parties → Pixi wheel payload (colors, avatars, TEAM normalization).
 * Shared with `generateCommands` and `WheelEngine.hydrateFromAckedRound` after navigation remount.
 */
export function mapSnapshotToWheelParties(
  snapshot: RoundSnapshot,
  userId?: string
): WheelParty[] {
  // Palette for solo sectors. Current user gets a stable highlighted color.
  const SOLO_PALETTE = [
    0xb1fe64, 0x7a2fff, 0xffd93d, 0x2f7bff, 0xff9a2f, 0x2fffcb, 0xff3d6e,
    0x3ddcff, 0xc77dff, 0xff6b6b, 0x4d96ff, 0xffc75f, 0x00c9a7, 0xf72585,
    0x90dbf4, 0xf77f00, 0x9b5de5, 0x00bbf9, 0xffbe0b, 0x06d6a0,
  ]

  const hashUserId = (id: string): number =>
    id
      .split("")
      .reduce(
        (h, c) => (Math.imul(h, 31) + c.charCodeAt(0)) | 0,
        0x811c9dc5
      ) >>> 0

  const soloColorMap = (() => {
    const map = new Map<string, number>()
    const used = new Set<number>()

    if (userId) {
      const mine = findSoloPartyForUser(snapshot, userId)
      if (mine) {
        map.set(mine.userId, SOLO_CURRENT_USER_COLOR)
        used.add(SOLO_CURRENT_USER_COLOR)
      }
    }

    for (const p of snapshot.parties) {
      if (isTeamRoundParty(p) || map.has(p.userId)) continue
      const base = hashUserId(p.userId ?? "") % SOLO_PALETTE.length
      let idx = base
      for (let attempt = 0; attempt < SOLO_PALETTE.length; attempt++) {
        if (!used.has(SOLO_PALETTE[idx]!)) break
        idx = (idx + 1) % SOLO_PALETTE.length
      }
      const color = SOLO_PALETTE[idx] ?? SOLO_PALETTE[0]!
      map.set(p.userId, color)
      used.add(color)
    }

    return map
  })()

  const partyColor = (p: RoundParty) => {
    if (isTeamRoundParty(p)) return TEAM_1_COLOR
    return soloColorMap.get(p.userId) ?? SOLO_PALETTE[0]!
  }

  const betAvatarMap = new Map<string, string | null>()
  const mergePhoto = (
    uid: string | undefined,
    photoUrl: string | null | undefined
  ) => {
    if (!uid) return
    const id = String(uid)
    const nextPhoto = photoUrl ?? null
    if (!betAvatarMap.has(id)) {
      betAvatarMap.set(id, nextPhoto)
      return
    }
    if (!betAvatarMap.get(id) && nextPhoto) {
      betAvatarMap.set(id, nextPhoto)
    }
  }
  for (const bet of snapshot.bets) {
    mergePhoto(bet.userData?.userId, bet.userData?.photoUrl)
  }
  for (const p of snapshot.parties) {
    for (const pb of p.playerBanks ?? []) {
      mergePhoto(pb.userData.userId, pb.userData.photoUrl)
    }
  }

  let parties: WheelParty[] = snapshot.parties.map((p) => ({
    userId: p.userId,
    username: p.username ?? null,
    sectorId:
      p.sectorId ??
      (typeof p.sectorFrom === "number" && typeof p.sectorTo === "number"
        ? `${p.sectorFrom}:${p.sectorTo}`
        : undefined),
    percent: parseFloat(
      p.percent ||
        (typeof p.winProbability === "number"
          ? String(p.winProbability * 100)
          : "0")
    ),
    betAmount: p.betAmount || ((p.totalBank ?? 0) / 1e9).toFixed(2),
    color: partyColor(p),
    partyType: isTeamRoundParty(p) ? "TEAM" : p.partyType,
    avatarUrl: betAvatarMap.get(String(p.userId)) ?? null,
    members: p.playerBanks?.map((pb) => ({
      userId: pb.userData.userId,
      username: pb.userData.username ?? null,
      avatarUrl: pb.userData.photoUrl,
    })),
  }))

  if (
    shouldApplyMutedWheelSectorColors(snapshot.status) &&
    userId &&
    userParticipatesInWheelRound(snapshot, userId)
  ) {
    parties = applyTricksPhaseSectorColors(parties, userId)
  }

  return parties
}

function generateCommands(
  diff: DomainDiffEvent[],
  prev: RoomSlice,
  next: RoundSnapshot,
  ctx: ReduceContext
): PixiCommand[] {
  const alignedNow = ctx.alignedNowMs ?? ctx.now
  const commands: PixiCommand[] = []
  const roomId = next.roundId
  const roundVersion = next.version
  const prevStatus = prev.rawRound?.status

  let cmdIdx = 0
  const nextId = (type: string) => makeCommandId(roundVersion, type, cmdIdx++)

  const toParties = () => mapSnapshotToWheelParties(next, ctx.userId)

  let emittedFinalize = false

  // First snapshot for room: init wheel (e.g. manual New round with empty parties)
  if (prev.rawRound == null) {
    commands.push({
      id: nextId("InitWheelScene"),
      roomId,
      roundVersion,
      type: "InitWheelScene",
      payload: { parties: toParties(), status: next.status },
    })
  }

  for (const ev of diff) {
    switch (ev.type) {
      case "PartiesChanged": {
        commands.push({
          id: nextId("UpdateSectorWeightsTween"),
          roomId,
          roundVersion,
          type: "UpdateSectorWeightsTween",
          payload: {
            durationMs: getPvpRouletteRuntime().SECTOR_TWEEN_DURATION_MS,
            parties: toParties(),
            status: next.status,
          },
        })
        break
      }
      case "StatusChanged": {
        if (ev.next === "TRICKS") {
          const t = getEffectiveTiming(next)
          const tricksEndMs = resolveTricksEndsAtMs(next)
          const targetDeg = centidegreesToDegrees(
            getEffectiveTargetCoordinate(next)
          )
          const tricksDurationMs =
            tricksEndMs != null
              ? Math.max(0, tricksEndMs - alignedNow)
              : undefined
          commands.push({
            id: nextId("EnterTricksPhase"),
            roomId,
            roundVersion,
            type: "EnterTricksPhase",
            payload: {
              tricksEndsAt: tricksEndMs ?? t.tricksEndsAt ?? undefined,
              ...(tricksDurationMs != null
                ? { durationMs: tricksDurationMs }
                : {}),
              targetAngleDeg: targetDeg,
            },
          })
        } else if (ev.next === "SLOWING") {
          emittedFinalize = true
          const targetCoord = getEffectiveTargetCoordinate(next)
          const targetDeg = centidegreesToDegrees(targetCoord)
          const durationMs = computeSlowingFinalizeDurationMs(alignedNow, next)
          commands.push({
            id: nextId("FinalizeToTarget"),
            roomId,
            roundVersion,
            type: "FinalizeToTarget",
            payload: {
              targetAngleDeg: targetDeg,
              easingKey: EASING_KEY.SPIN_OUT,
              ...(durationMs != null ? { durationMs } : {}),
            },
          })
        } else if (ev.next === "RESOLVED") {
          // After SLOWING the wheel is already at roulette_position; only show overlay.
          if (ev.prev !== "SLOWING") {
            emittedFinalize = true
            const targetCoord = getEffectiveTargetCoordinate(next)
            const targetDeg = centidegreesToDegrees(targetCoord)
            commands.push({
              id: nextId("FinalizeToTarget"),
              roomId,
              roundVersion,
              type: "FinalizeToTarget",
              payload: {
                targetAngleDeg: targetDeg,
                easingKey: EASING_KEY.SPIN_OUT,
              },
            })
          }
        }
        break
      }
      case "TrickAdded":
      case "TrickUpdated": {
        if (next.status === "RESOLVED" || next.status === "SLOWING") break
        const tricksEndMs = resolveTricksEndsAtMs(next)
        if (tricksEndMs == null) break
        const isFreeze = ev.trick.type === TRICK_TYPE_FREEZE_WHEEL
        // Must match optimistic key shape in applyTrickOptimistic so the
        // WS-echo command dedupes against the optimistic impulse.
        const trickEffectKey = `${ev.trick.userId}:${ev.trick.type}`
        commands.push({
          id: nextId(isFreeze ? "ApplyTrickFreeze" : "ApplyTrickPush"),
          roomId,
          roundVersion,
          type: isFreeze ? "ApplyTrickFreeze" : "ApplyTrickPush",
          payload: {
            tricksEndsAt: tricksEndMs,
            trickEffectKey,
          },
        })
        break
      }
      case "TargetCoordinateChanged": {
        if (next.status === "TRICKS") {
          const syncDeg = centidegreesToDegrees(
            getEffectiveTargetCoordinate(next)
          )
          commands.push({
            id: nextId("SyncPlannedFinalizeTarget"),
            roomId,
            roundVersion,
            type: "SyncPlannedFinalizeTarget",
            payload: { targetAngleDeg: syncDeg },
          })
        }
        if (
          next.status === "RESOLVED" &&
          !emittedFinalize &&
          prevStatus !== "SLOWING"
        ) {
          emittedFinalize = true
          const targetDeg = centidegreesToDegrees(
            getEffectiveTargetCoordinate(next)
          )
          commands.push({
            id: nextId("FinalizeToTarget"),
            roomId,
            roundVersion,
            type: "FinalizeToTarget",
            payload: {
              targetAngleDeg: targetDeg,
              easingKey: EASING_KEY.SPIN_OUT,
            },
          })
        }
        break
      }
      default:
        break
    }
  }

  const timing = getEffectiveTiming(next)
  const prevSpinEnd = prev.rawRound
    ? getEffectiveTiming(prev.rawRound).spinningEndsAt
    : null
  const nextSpinEnd = timing.spinningEndsAt
  const shouldEmitStartSpin =
    next.status === "SPINNING" &&
    nextSpinEnd != null &&
    (prev.rawRound == null ||
      prev.rawRound.status !== "SPINNING" ||
      prevSpinEnd == null)

  if (shouldEmitStartSpin) {
    const targetDeg = centidegreesToDegrees(getEffectiveTargetCoordinate(next))
    const spinDurationMs =
      nextSpinEnd != null
        ? Math.max(0, normalizeUnixTimestampToMs(nextSpinEnd) - alignedNow)
        : undefined
    commands.push({
      id: nextId("StartSpin"),
      roomId,
      roundVersion,
      type: "StartSpin",
      payload: {
        targetAngleDeg: targetDeg,
        easingKey: EASING_KEY.LINEAR,
        spinningEndsAt: nextSpinEnd,
        ...(spinDurationMs != null ? { durationMs: spinDurationMs } : {}),
      },
    })
  }

  if (next.status === "RESOLVED" && prevStatus !== "RESOLVED") {
    const winnerId = next.winnerPartyId
    commands.push({
      id: nextId("Resolve"),
      roomId,
      roundVersion,
      type: "Resolve",
      payload: { winnerId },
    })
  }

  return commands
}

function sumUserActiveBets(
  snapshot: RoundSnapshot | null,
  userId: string
): number {
  if (!snapshot) return 0
  const active = getActiveBets(snapshot.bets)
  return active
    .filter((b) => b.userData.userId === userId)
    .reduce((sum, b) => sum + (parseFloat(b.amount || "0") || 0), 0)
}

function reconcilePending(
  pending: OptimisticPendingBet | null,
  prevSnapshot: RoundSnapshot | null,
  snapshot: RoundSnapshot,
  now: number,
  userId?: string
): OptimisticPendingBet | null {
  if (!pending) return null
  if (pending.submittedAt + pending.ttlMs < now) return null // TTL expired

  if (!userId) return pending

  // Reconcile by growth of the user's ACTIVE/PENDING bank between snapshots.
  // This avoids false-positive clears when the user already has an older bet
  // with the same amount (e.g. multiple 0.1 TON bets in one round).
  const prevUserTotal = sumUserActiveBets(prevSnapshot, userId)
  const nextUserTotal = sumUserActiveBets(snapshot, userId)
  const expectedMin = prevUserTotal + pending.amount - 0.001
  if (nextUserTotal >= expectedMin) return null

  return pending
}
